import { Download, Printer } from 'lucide-react';
import type { RateList, RateListGroup } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useApiResource } from '#client/hooks/useResourceItem';
import { formatMoney, countOf } from '#client/lib/format';
import { ENDPOINT, printUrls } from '#client/lib/endpoints';

type Entry = RateListGroup['tests'][number];

const columns: Column<Entry>[] = [
  { key: 'name', header: 'Test', render: (row) => row.name, frozen: true },
  {
    key: 'code',
    header: 'Code',
    width: '130px',
    render: (row) => <span className="ident">{row.code}</span>,
  },
  {
    key: 'turnaround_hours',
    header: 'Turnaround',
    width: '130px',
    render: (row) =>
      row.turnaround_hours === null ? (
        <span className="text-fg-muted">—</span>
      ) : (
        <span className="tnum">{row.turnaround_hours} h</span>
      ),
  },
  {
    key: 'price',
    header: 'Price',
    width: '120px',
    align: 'end',
    render: (row) => <span className="tnum">{formatMoney(row.price)}</span>,
  },
];

/**
 * The price list, grouped by bench.
 *
 * Deliberately not a `ResourceListPage`: there is nothing to create here, no
 * search box and no pagination — it is a document, and a price list with some of
 * the prices on another page is not one.
 */
export function RateListPage() {
  const rateList = useApiResource<RateList>(ENDPOINT.rateList);

  if (rateList.loading) return <PageLoading title="Rate list" fullWidth />;
  if (rateList.error || !rateList.data) {
    return (
      <PageMessage title="Rate list" fullWidth>
        {rateList.error ?? 'Could not load the rate list.'}
      </PageMessage>
    );
  }

  const { groups, test_count } = rateList.data;
  const populated = groups.filter((group) => group.tests.length > 0);

  return (
    <Page
      title="Rate list"
      subtitle={`${countOf(test_count, 'test')} across ${countOf(populated.length, 'department')}`}
      fullWidth
      // Real links, not fetches: the endpoints return bytes, and the browser's
      // own viewer is a better PDF reader than anything worth building here.
      // Styled with the button classes directly, the way PdfFrame's download
      // link already is — an `<a>` keeps middle-click and "save as".
      primaryAction={
        <a
          className="c-button c-button--primary c-button--md"
          href={printUrls.rateList()}
          target="_blank"
          rel="noopener"
        >
          <Printer aria-hidden />
          Print
        </a>
      }
      secondaryActions={
        <a
          className="c-button c-button--secondary c-button--md"
          href={printUrls.rateList(true)}
          download
        >
          <Download aria-hidden />
          Download
        </a>
      }
    >
      {populated.length === 0 ? (
        <EmptyState
          title="No tests in the catalogue yet"
          body="Add lab tests and give them a department, and they will appear here grouped and priced."
        />
      ) : (
        <div className="flex flex-col gap-5">
          {populated.map((group) => (
            <Card
              key={group.department_id ?? 'uncategorised'}
              title={group.name}
              subtitle={`${countOf(group.tests.length, 'test')}`}
              flush
            >
              <DataTable
                columns={columns}
                rows={group.tests}
                rowKey={(row) => row.id}
                caption={`${group.name} tests and prices`}
                stickyHeader={false}
              />
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
